"use client";

import { useDynamicTextareaSize } from "@shared/lib/hooks/use-dynamic-textarea-size";
import { useRef, useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { resolveOptions } from "./utils";
import { Input } from "@shared/components/shadcn/input";
import { Textarea } from "@shared/components/shadcn/textarea";
import { Slider } from "@shared/components/shadcn/slider";
import RadioOption from "./radio-option";
import CheckboxOption from "./checkbox-option";
import Required from "./required";
import IconCardThemeRadio from "./icon-card-theme-radio";
import IconCardThemeCheckbox from "./icon-card-theme-checkbox";
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from "@shared/components/shadcn/form";
import { Alert, AlertDescription } from "@shared/components/shadcn/alert";
import { AlertCircle } from "lucide-react";
import { Question } from "@/types/assessment.types";

// Reusable Error Alert Component for Questions
function QuestionErrorAlert({ message }: { message: string }) {
  return (
    <div className="max-w-sm">
      <Alert variant="destructive" className="h-auto bg-red-50 border-red-200 !px-4 py-3 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <AlertDescription className="text-red-700 text-sm leading-relaxed">
            {message}
          </AlertDescription>
        </div>
      </Alert>
    </div>
  );
}

type PromptQuestionProps = {
  question: Question;
  index: number;
  answers?: Record<string, unknown>;
  questions?: Question[];
};
const INPUT_TYPES = ["text", "short-text", "scale", "radio", "checkbox"];

export function PromptQuestion({
  question,
  index,
  answers = {},
  questions = [],
}: PromptQuestionProps) {
  const { placeholder, answer, question: questionText, instruction, type, typeOptions, required } = question;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const didAutoSelectRef = useRef(false); // Guard single-choice auto-select
  useDynamicTextareaSize(textareaRef as React.RefObject<HTMLTextAreaElement>, text);
  const finalPlaceholder = placeholder || "Type your answer here...";
  const form = useFormContext();
  const OTHER_SENTINEL = "__other__";

  // Reset text when question index changes
  useEffect(() => {
    if (type === 'text' || type === 'short-text') {
      setText('');
    }
  }, [index, type]);

  useEffect(() => {
    if (type === 'checkbox') {
      setSelectedValues(Array.isArray(answer) ? answer : []);
    } else if (type === 'radio') {
      // For radio, set the text from answer
      setText(typeof answer === 'string' ? answer : answer?.toString() || '');
    } else if (type === 'scale') {
      // For scale, only set text if answer is numeric or can be converted to a valid number
      if (answer !== undefined && answer !== '') {
        const numericValue = Number(answer);
        if (!isNaN(numericValue)) {
          setText(answer.toString());
        } else {
          // If answer is not numeric, don't set it as text for scale
          setText('');
        }
      } else {
        setText('');
      }
    } else if ((type === 'text' || type === 'short-text') && answer !== undefined && answer !== '') {
      // For text inputs, only set if we have a valid non-empty answer for this specific question
      setText(typeof answer === 'string' ? answer : '');
    }
  }, [answer, type]); // Removed 'text' from dependency to avoid infinite loops

  // Reset auto-select guard when question index changes
  useEffect(() => {
    didAutoSelectRef.current = false;
  }, [index]);

  // Initialize scale value to min on mount if not already set
  // This ensures the visual default matches the form value
  useEffect(() => {
    if (type === 'scale') {
      const fieldName = question.id || `question-${index}-scale`;
      const currentValue = form.getValues(fieldName);
      if (currentValue === undefined || currentValue === '') {
        const defaultValue = (typeOptions?.scale?.min || 1).toString();
        form.setValue(fieldName, defaultValue);
        setText(defaultValue);
      }
    }
  }, [type, index, question.id, typeOptions?.scale?.min, form]);

  // Resolve options (supports dynamic mapping)
  const radioOptions = type === 'radio' ? resolveOptions(question, answers, questions, 'radio') : (typeOptions?.radio ? typeOptions.radio.options : []);
  const checkboxOptions = type === 'checkbox' ? resolveOptions(question, answers, questions, 'checkbox') : (typeOptions?.checkbox ? typeOptions.checkbox.options : []);

  console.log("📋 [DEBUG] Base options from typeOptions:");
  console.log("  - typeOptions:", typeOptions);
  console.log("  - Initial radioOptions:", radioOptions);
  console.log("  - Initial checkboxOptions:", checkboxOptions);

  // Process dynamic question text and options
  let processedQuestion = questionText;
  const dynamicConfig = question.dynamicConfig as {
    optionsBasedOn?: string;
    optionsMapping?: Record<string, Array<{ value: string; icon?: string; description?: string }>>;
  } | undefined;

  // Create answer mapping by question ID for easy lookup
  const answersByQuestionId: Record<string, unknown> = {};
  // First, try to map by actual question IDs if they exist
  Object.entries(answers).forEach(([key, value]) => {
    // Check if this key matches a question ID
    const matchingQuestion = questions.find(q => 'id' in q && q.id === key);
    if (matchingQuestion) {
      answersByQuestionId[key] = value;
    }
  });
  // Also support index-based keys for backward compatibility
  questions.forEach((q, idx) => {
    const key = `question-${idx}-${q.type}`;
    const questionId = 'id' in q ? q.id : undefined;
    if (answers[key] !== undefined && questionId && !answersByQuestionId[questionId]) {
      answersByQuestionId[questionId] = answers[key];
    }
  });

  console.log("🔍 [DEBUG] Question processing for index:", index);
  console.log("🔍 [DEBUG] Current question:", { question, type, index });
  console.log("🔍 [DEBUG] All answers:", answers);
  console.log("🔍 [DEBUG] Answers by question ID:", answersByQuestionId);
  console.log("🔍 [DEBUG] Dynamic config:", dynamicConfig);

  // Generic text interpolation for question text
  if (processedQuestion && processedQuestion.includes('{{') && processedQuestion.includes('}}')) {
    processedQuestion = processedQuestion.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
      const value = answersByQuestionId[fieldName] || answers[fieldName] || fieldName;
      return String(value);
    });
  }

  // Dynamic options replaced by resolveOptions above.
  const maxSelections = typeOptions?.checkbox?.maxSelections;

  console.log("🎭 [DEBUG] Final options for rendering:");
  console.log("  - radioOptions:", radioOptions);
  console.log("  - checkboxOptions:", checkboxOptions);
  console.log("  - Question type:", type);

  

  

  return (
    <FormField
      rules={{
        required: required ? "This field is required" : false,
        onChange: (e) => {
          if (type === 'checkbox') {
            if (Array.isArray(e)) {
              setSelectedValues(e);
            }
          } else if (type !== 'radio' && e?.target?.value !== undefined) {
            // Update text for all types except radio (which has custom handling)
            setText(e.target.value);
          }
        }
      }}
      name={`question-${index}-${type}`}
      render={({ field, fieldState: { error } }) => (
        <FormItem>
          <FormLabel className={`text-xl mb-2 ${error && "text-red-800"} whitespace-pre-line`}>
            {processedQuestion || questionText}
            {required && <Required />}
          </FormLabel>
          {instruction && <FormDescription className="text-gray-500 mb-4">{instruction}</FormDescription>}
          {!instruction && <div className="mb-4" />}
          <FormControl>
            {INPUT_TYPES.includes(type) ? (
              <div>
                {type === "short-text" && (
                  <Input
                    {...field}
                    value={text}
                    placeholder={finalPlaceholder}
                    className="text-base sm:text-md bg-[#D2CED4]/10 border-2 border-[#BFBCC0]/50 max-w-2xl focus:border-[#2A6354] focus:ring-2 focus:ring-[#2A6354]/20 transition-all duration-200 px-4 py-3 sm:py-2"
                  />
                )}
                {type === "text" && (
                  <Textarea
                    {...field}
                    ref={textareaRef}
                    value={text}
                    className="text-base sm:text-md min-h-[120px] sm:min-h-[100px] w-full bg-[#D2CED4]/10 border-2 border-[#BFBCC0]/50 focus:border-[#2A6354] focus:ring-2 focus:ring-[#2A6354]/20 transition-all duration-200 px-4 py-3 resize-none"
                    placeholder={finalPlaceholder}
                  />
                )}

                {type === "scale" && (
                  <div className="flex flex-col sm:flex-row items-center gap-4 px-4 sm:px-0 [.assessment-question_&]:justify-center [.assessment-question_&]:mt-6">
                    <Slider
                      className="w-full max-w-xs sm:max-w-md [&_[role=slider]]:bg-[#2A6354] [&_[role=slider]]:border-[#2A6354] [&_.bg-primary]:bg-[#2A6354]"
                      max={typeOptions?.scale?.max}
                      min={typeOptions?.scale?.min}
                      step={1}
                      value={[field.value && !isNaN(parseInt(field.value)) ? parseInt(field.value) : typeOptions?.scale?.min || 1]}
                      onValueChange={(vals) => {
                        const newValue = vals[0].toString();
                        setText(newValue);
                        field.onChange(newValue);
                      }}
                    />
                    <span className="leading-none text-xl sm:text-lg font-semibold text-[#2A6354] min-w-[2rem] text-center">
                      {field.value && !isNaN(parseInt(field.value)) ? field.value : typeOptions?.scale?.min || 1}
                    </span>
                  </div>
                )}
                {type === "radio" && (
                  (typeOptions?.radio as { theme?: string })?.theme === 'icon-card' ? (
                    (() => {
                      // Auto-select single non-custom option
                      if (
                        !didAutoSelectRef.current &&
                        (!field.value || field.value === '') &&
                        radioOptions.length === 1 &&
                        radioOptions[0] && !radioOptions[0].allowCustomInput
                      ) {
                        didAutoSelectRef.current = true;
                        const singleVal = radioOptions[0].value;
                        setText(singleVal);
                        field.onChange(singleVal);
                      }
                      const selectedForDisplay = field.value === OTHER_SENTINEL
                        ? (radioOptions.find(o => o.allowCustomInput)?.value || '')
                        : (field.value || text);
                      return (
                        <IconCardThemeRadio
                          options={radioOptions}
                          value={selectedForDisplay}
                          onChange={(value) => {
                            const clicked = radioOptions.find(o => o.value === value);
                            if (clicked?.allowCustomInput) {
                              // Map to sentinel
                              field.onChange(OTHER_SENTINEL);
                            } else {
                              setText(value);
                              field.onChange(value);
                            }
                          }}
                          name={field.name}
                        />
                      );
                    })()
                  ) : (
                    <div className="flex flex-col gap-2 max-w-max text-left">
                      {(() => {
                        const otherFieldName = `${field.name}__other`;

                        // If only one non-custom option is available, preselect and trigger auto-progress via watcher
                        if (
                          !didAutoSelectRef.current &&
                          (!field.value || field.value === '') &&
                          radioOptions.length === 1 &&
                          radioOptions[0] && !radioOptions[0].allowCustomInput
                        ) {
                          didAutoSelectRef.current = true;
                          const singleVal = radioOptions[0].value;
                          setText(singleVal);
                          field.onChange(singleVal);
                        }

                        return radioOptions.map((opt, k) => {
                          // Selection logic via sentinel for allowCustomInput
                          const isSelected = opt.allowCustomInput ? field.value === OTHER_SENTINEL : field.value === opt.value;

                          return (
                            <RadioOption
                              option={opt}
                              isSelected={isSelected}
                              index={k}
                              key={questionText + type + opt.value}
                              name={field.name}
                              customValue={form.getValues(otherFieldName) || ""}
                              onCustomChange={(val) => form.setValue(otherFieldName, val)}
                              sentinelOtherValue={OTHER_SENTINEL}
                              onChange={(value) => {
                                if (opt.allowCustomInput) {
                                  field.onChange(OTHER_SENTINEL);
                                } else {
                                  field.onChange(value);
                                }
                              }}
                            />
                          );
                        });
                      })()}
                    </div>
                  )
                )}
                {type === "checkbox" && (
                  (typeOptions?.checkbox as { theme?: string })?.theme === 'icon-card' ? (
                    <IconCardThemeCheckbox
                      options={checkboxOptions}
                      value={selectedValues}
                      onChange={(newValues) => {
                        setSelectedValues(newValues);
                        field.onChange(newValues);
                      }}
                      maxSelections={maxSelections}
                      name={field.name}
                    />
                  ) : (
                    <div className="flex flex-col gap-2 max-w-max text-left">
                      {checkboxOptions.map((opt, k) => (
                        <CheckboxOption
                          option={opt}
                          isSelected={selectedValues.includes(opt.value)}
                          index={k}
                          key={questionText + type + opt.value}
                          value={selectedValues}
                          onChange={(newValues) => {
                            if (!maxSelections || newValues.length <= maxSelections) {
                              setSelectedValues(newValues);
                              field.onChange(newValues);
                            }
                          }}
                          name={field.name}
                          onBlur={field.onBlur}
                        />
                      ))}
                      {maxSelections && selectedValues.length >= maxSelections && (
                        <FormDescription className="text-sm text-muted-foreground mt-2">
                          Maximum {maxSelections} selection{maxSelections > 1 ? 's' : ''} allowed
                        </FormDescription>
                      )}
                    </div>
                  )
                )}
              </div>
            ) : (
              <Textarea
                {...field}
                ref={textareaRef}
                value={text}
                className="h-[30px] w-full bg-gray-100 border-none"
                placeholder={finalPlaceholder}
              />
            )}
          </FormControl>
          {error && <QuestionErrorAlert message={error.message || "An error occurred"} />}
        </FormItem>
      )}
    />
  );
}